
import { Button } from "@/components/ui/button";
import pokerChip from "@/assets/poker-chip.png";
import { MobileFeatureCarousel } from "@/components/MobileFeatureCarousel";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { usePWAStandalone } from "@/hooks/usePWAStandalone";

interface LandingPageProps {
  onGetStarted: () => void;
}

export const LandingPage = ({ onGetStarted }: LandingPageProps) => {
  const isStandalone = usePWAStandalone();

  return (
    <div className="min-h-screen bg-background">
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Header */}
      <header className="container mx-auto px-6 py-4">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={pokerChip} alt="SplitPot" className="w-8 h-8" />
            <h1 className="text-2xl font-bold text-foreground">SplitPot</h1>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onGetStarted}>Sign In</Button>
            <Button onClick={onGetStarted}>Get Started</Button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-16 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold text-foreground mb-6">
            Perfect Poker Payouts, <span className="text-primary">Every Time</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Stop the guesswork. Calculate accurate payouts for live poker games with multiple buy-ins, 
            track player stats, and settle games fairly with mathematical precision.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button size="lg" className="px-8" onClick={onGetStarted}>
              Start Your First Game
            </Button>
            <Button variant="outline" size="lg" onClick={() => document.querySelector('[data-section="how-it-works"]')?.scrollIntoView({ behavior: 'smooth' })}>
              See How It Works
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-foreground mb-4">
            Everything You Need for Fair Poker Nights
          </h3>
          <p className="text-muted-foreground text-lg">
            Built by poker players, for poker players
          </p>
        </div>

        <MobileFeatureCarousel />
      </section>

      {/* How It Works */}
      <section className="bg-muted py-16" data-section="how-it-works">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">
              Simple 4-Step Process
            </h3>
            <p className="text-muted-foreground text-lg">
              From game start to final payout in minutes
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl mx-auto mb-4">
                1
              </div>
              <h4 className="text-lg font-semibold mb-2">Create Game</h4>
              <p className="text-sm text-muted-foreground">
                Set buy-in amount and chip value ratio
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl mx-auto mb-4">
                2
              </div>
              <h4 className="text-lg font-semibold mb-2">Track Buy-ins</h4>
              <p className="text-sm text-muted-foreground">
                Log rebuys and add-ons as they happen
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl mx-auto mb-4">
                3
              </div>
              <h4 className="text-lg font-semibold mb-2">Enter Final Chips</h4>
              <p className="text-sm text-muted-foreground">
                Each player enters their ending chip count
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-xl mx-auto mb-4">
                4
              </div>
              <h4 className="text-lg font-semibold mb-2">Get Payouts</h4>
              <p className="text-sm text-muted-foreground">
                Instant payout calculations with export options
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-16 text-center">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-3xl font-bold text-foreground mb-4">
            Ready to Simplify Your Poker Nights?
          </h3>
          <p className="text-muted-foreground text-lg mb-8">
            Join thousands of poker players who trust SplitPot for accurate, fair payouts
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button size="lg" className="px-8" onClick={onGetStarted}>
              Create Free Account
            </Button>
            <Button variant="outline" size="lg" onClick={() => alert('Demo game coming soon!')}>
              View Demo Game
            </Button>
          </div>
        </div>
      </section>

      {/* Footer - Only show if not in PWA standalone mode */}
      {!isStandalone && (
        <footer className="border-t border-border bg-muted">
          <div className="container mx-auto px-6 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={pokerChip} alt="SplitPot" className="w-6 h-6" />
                <span className="text-sm text-muted-foreground">
                  © 2025 SplitPot
                </span>
              </div>
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
                <a href="#" className="hover:text-foreground transition-colors">Terms</a>
                <a href="mailto:prazeresryan@gmail.com" className="hover:text-foreground transition-colors">Support</a>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};
